// Indicator math ported verbatim from static/app.js. Framework-agnostic and pure
// so it can be unit-tested directly (see indicators.test.ts).
import type { Candle } from "../api/types";

export interface LinePoint {
  time: number;
  value: number;
}

export interface BollingerBands {
  upper: LinePoint[];
  middle: LinePoint[];
  lower: LinePoint[];
}

export const INDICATORS = [
  { key: "sma20", label: "SMA 20", color: "#f59e0b" },
  { key: "sma50", label: "SMA 50", color: "#7c3aed" },
  { key: "ema20", label: "EMA 20", color: "#0ea5e9" },
  { key: "ema50", label: "EMA 50", color: "#db2777" },
  { key: "bb20", label: "Bollinger Bands 20", color: "#475569" },
  { key: "vwap", label: "VWAP", color: "#0f766e" },
] as const;

export type IndicatorKey = (typeof INDICATORS)[number]["key"];

export function getIndicatorDefinition(indicatorKey: string) {
  return INDICATORS.find((indicator) => indicator.key === indicatorKey);
}

export function calculateSma(candles: Candle[], values: number[], period: number): LinePoint[] {
  const points: LinePoint[] = [];
  let rollingTotal = 0;

  values.forEach((value, index) => {
    if (!Number.isFinite(value)) {
      return;
    }

    rollingTotal += value;
    if (index >= period) {
      rollingTotal -= values[index - period];
    }
    if (index >= period - 1) {
      points.push({
        time: candles[index].time,
        value: rollingTotal / period,
      });
    }
  });

  return points;
}

export function calculateEma(candles: Candle[], values: number[], period: number): LinePoint[] {
  const points: LinePoint[] = [];
  const smoothing = 2 / (period + 1);
  let ema: number | null = null;
  let seedTotal = 0;

  values.forEach((value, index) => {
    if (!Number.isFinite(value)) {
      return;
    }

    if (index < period - 1) {
      seedTotal += value;
      return;
    }

    if (index === period - 1) {
      seedTotal += value;
      ema = seedTotal / period;
    } else {
      ema = value * smoothing + (ema as number) * (1 - smoothing);
    }

    points.push({
      time: candles[index].time,
      value: ema as number,
    });
  });

  return points;
}

export function calculateBollingerBands(
  candles: Candle[],
  period: number,
  deviationMultiplier: number,
): BollingerBands {
  const upper: LinePoint[] = [];
  const middle: LinePoint[] = [];
  const lower: LinePoint[] = [];
  const closes = candles.map((candle) => Number(candle.close));

  closes.forEach((close, index) => {
    if (!Number.isFinite(close) || index < period - 1) {
      return;
    }

    const windowValues = closes.slice(index - period + 1, index + 1);
    if (windowValues.some((value) => !Number.isFinite(value))) {
      return;
    }

    const average = windowValues.reduce((total, value) => total + value, 0) / period;
    const variance =
      windowValues.reduce((total, value) => total + Math.pow(value - average, 2), 0) / period;
    const deviation = Math.sqrt(variance) * deviationMultiplier;
    const time = candles[index].time;

    upper.push({ time, value: average + deviation });
    middle.push({ time, value: average });
    lower.push({ time, value: average - deviation });
  });

  return { upper, middle, lower };
}

export function calculateVwap(candles: Candle[]): LinePoint[] {
  const points: LinePoint[] = [];
  let cumulativePriceVolume = 0;
  let cumulativeVolume = 0;

  candles.forEach((candle) => {
    const high = Number(candle.high);
    const low = Number(candle.low);
    const close = Number(candle.close);
    const volume = Number(candle.volume ?? 0);

    if (!Number.isFinite(high) || !Number.isFinite(low) || !Number.isFinite(close) || volume <= 0) {
      return;
    }

    cumulativePriceVolume += ((high + low + close) / 3) * volume;
    cumulativeVolume += volume;
    points.push({
      time: candle.time,
      value: cumulativePriceVolume / cumulativeVolume,
    });
  });

  return points;
}

export function calculateIndicatorData(indicatorKey: string, candles: Candle[]): LinePoint[] {
  const closes = candles.map((candle) => Number(candle.close));
  switch (indicatorKey) {
    case "sma20":
      return calculateSma(candles, closes, 20);
    case "sma50":
      return calculateSma(candles, closes, 50);
    case "ema20":
      return calculateEma(candles, closes, 20);
    case "ema50":
      return calculateEma(candles, closes, 50);
    case "vwap":
      return calculateVwap(candles);
    default:
      return [];
  }
}
